// src/pages/Les1Dashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, Button } from "../components/ui/UIComponents";
import {
  LES1_CONFIG,
  getAvailableModules,
  getModuleById,
} from "../lessons/les1-motiverende-gespreksvoering/config";
import { lessonsAPI } from "../services/api";

const Les1Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user progress from API
  useEffect(() => {
    const fetchUserProgress = async () => {
      try {
        const data = await lessonsAPI.getProgress("les1");

        // Convert API progress format to our component format
        const progressArray = data.progress.completedModules.map(
          (moduleId) => ({
            moduleId,
            completed: true,
            completedAt: data.progress.moduleProgress?.[moduleId]?.completedAt,
          }),
        );

        setUserProgress(progressArray);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching progress:", err);
        setError("Kon voortgang niet laden");
        setUserProgress([]);
        setLoading(false);
      }
    };

    fetchUserProgress();
  }, []);

  const availableModules = getAvailableModules(userProgress);
  const completedModuleIds = userProgress.map((p) => p.moduleId);

  const getModuleStatus = (module) => {
    if (completedModuleIds.includes(module.id)) {
      return "completed";
    }
    if (availableModules.some((m) => m.id === module.id)) {
      return "available";
    }
    return "locked";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "border-green-500 bg-green-50";
      case "available":
        return "border-purple-500 bg-purple-50 hover:bg-purple-100";
      case "locked":
        return "border-charcoal-300 bg-cream-100";
      default:
        return "border-charcoal-300";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return "✅";
      case "available":
        return "▶️";
      case "locked":
        return "🔒";
      default:
        return "";
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem("ilearn_token");
    }
    navigate("/login");
  };

  const handleModuleClick = (module) => {
    const status = getModuleStatus(module);
    if (status === "available" || status === "completed") {
      navigate(`/les1/${module.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cream-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-charcoal-600 font-body">Dashboard laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <Card className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-charcoal-800 mb-2 font-header">
            Er is een fout opgetreden
          </h2>
          <p className="text-charcoal-600 mb-4 font-body">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Opnieuw proberen
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="container mx-auto px-4 py-8">
        {/* Back Link */}
        <div className="mb-4">
          <Link
            to="/dashboard"
            className="text-purple-600 hover:text-purple-800 font-medium inline-flex items-center"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Terug naar alle trainingen
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-title text-5xl font-light text-purple-600 mb-4">
            Haal meer uit verandertaal!
          </h1>
          <p className="font-header text-xl text-charcoal-700">
            Open vragen stellen en reflecteren om verandertaal te versterken
          </p>
        </div>

        {/* Top actions */}
        <div className="flex justify-end mb-6">
          <Button onClick={handleLogout}>Log uit</Button>
        </div>

        {/* Lesson Overview */}
        <Card className="mb-8">
          <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-4">
            {LES1_CONFIG.title}
          </h2>
          <p className="text-charcoal-600 mb-4 font-body">
            {LES1_CONFIG.description}
          </p>
          <div className="flex items-center space-x-4 text-sm text-charcoal-500 font-body">
            <span>⏱️ {LES1_CONFIG.totalDuration} minuten totaal</span>
            <span>📚 {LES1_CONFIG.modules.length} modules</span>
            <span>✅ {completedModuleIds.length} voltooid</span>
          </div>
        </Card>

        {/* Main Modules */}
        <div className="mb-8">
          <h3 className="font-header text-2xl font-medium text-charcoal-800 mb-6">
            Modules
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {LES1_CONFIG.modules.map((module) => {
              const status = getModuleStatus(module);
              const isClickable =
                status === "available" || status === "completed";

              return (
                <Card
                  key={module.id}
                  className={`rounded-none transition-all duration-200 cursor-pointer transform hover:scale-105 ${getStatusColor(status)} ${
                    !isClickable ? "cursor-not-allowed opacity-75" : ""
                  }`}
                  onClick={() => handleModuleClick(module)}
                  hover={isClickable}
                >
                  <div className="flex items-start justify-end mb-4">
                    <div className="text-2xl">{getStatusIcon(status)}</div>
                  </div>

                  <h4 className="font-header text-xl font-medium text-charcoal-800 mb-2">
                    {module.title}
                  </h4>

                  <p className="text-charcoal-600 mb-4 font-body">
                    {module.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-charcoal-500 font-body">
                      ⏱️ {module.duration} min
                    </span>
                    <span
                      className={`px-3 py-1 rounded-none text-sm font-medium font-body ${
                        status === "completed"
                          ? "bg-green-100 text-green-800"
                          : status === "available"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-charcoal-100 text-charcoal-800"
                      }`}
                    >
                      {status === "completed"
                        ? "Voltooid"
                        : status === "available"
                          ? "Beschikbaar"
                          : "Vergrendeld"}
                    </span>
                  </div>

                  {status === "locked" && module.prerequisites.length > 0 && (
                    <div className="mt-3 p-2 bg-cream-100 rounded-none text-sm text-charcoal-600 font-body">
                      <strong>Vereist:</strong>{" "}
                      {module.prerequisites
                        .map((prereq) => {
                          const prereqModule = getModuleById(prereq);
                          return prereqModule?.title || prereq;
                        })
                        .join(", ")}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Extra Modules */}
        {LES1_CONFIG.extraModules.length > 0 && (
          <div>
            <h3 className="font-header text-2xl font-medium text-charcoal-800 mb-6">
              Extra Modules
            </h3>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {LES1_CONFIG.extraModules.map((module) => {
                const status = getModuleStatus(module);
                const isClickable =
                  status === "available" || status === "completed";

                return (
                  <Card
                    key={module.id}
                    className={`rounded-none transition-all duration-200 cursor-pointer transform hover:scale-105 ${getStatusColor(status)} ${
                      !isClickable ? "cursor-not-allowed opacity-75" : ""
                    }`}
                    onClick={() => handleModuleClick(module)}
                    hover={isClickable}
                  >
                    <div className="flex items-start justify-end mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-none font-body">
                          OPTIONEEL
                        </span>
                        <div className="text-2xl">{getStatusIcon(status)}</div>
                      </div>
                    </div>

                    <h4 className="font-header text-xl font-medium text-charcoal-800 mb-2">
                      {module.title}
                    </h4>

                    <p className="text-charcoal-600 mb-4 font-body">
                      {module.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-charcoal-500 font-body">
                        ⏱️ {module.duration} min
                      </span>
                      <span
                        className={`px-3 py-1 rounded-none text-sm font-medium font-body ${
                          status === "completed"
                            ? "bg-green-100 text-green-800"
                            : status === "available"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-charcoal-100 text-charcoal-800"
                        }`}
                      >
                        {status === "completed"
                          ? "Voltooid"
                          : status === "available"
                            ? "Beschikbaar"
                            : "Vergrendeld"}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Progress Summary */}
        {userProgress.length > 0 && (
          <Card className="mt-8">
            <h3 className="font-header text-xl font-medium text-charcoal-800 mb-4">
              Je Voortgang
            </h3>
            <div className="space-y-3">
              {userProgress.map((progress, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-cream-100 rounded-lg"
                >
                  <span className="font-medium text-charcoal-800 font-body">
                    {getModuleById(progress.moduleId)?.title ||
                      progress.moduleId}
                  </span>
                  <div className="flex items-center space-x-2">
                    {progress.score && (
                      <span className="text-sm text-charcoal-600 font-body">
                        Score: {progress.score}/10
                      </span>
                    )}
                    <span className="text-green-600">✅</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Les1Dashboard;
